import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { ReactNode } from 'react';
import type { LottoPotGaugeState } from '../lucky-ticket/_utils/resolveLottoPotGauge';
import styles from './LottoPotGauge.module.scss';
interface LottoPotGaugeProps extends LottoPotGaugeState {
  /** 누적 금액 표기(로딩 폴백은 '-') */
  accumulatedAmountLabel: ReactNode;
  /** 하단 목표금액 행 좌측 슬롯(허브 카드의 응모권 카운터) */
  leading?: ReactNode;
  /**
   * 축하 연출(막대 글로우 심장박동) 여부. `isGoalReached`(공동 풀 상태)와 별개로,
   * 실제로 응모 가능한 사용자에게만 켜라 — 라벨은 풀을 말하고 연출은 행동 가능을 말한다.
   */
  isCelebrating?: boolean;
}

/**
 * 행운의 로또 누적 상금 게이지(Figma 9756-23026).
 * 그라디언트 채움 끝에 누적 금액 말풍선이 앵커되고(0%면 좌측·100%면 우측 정렬로 트랙 안에 머문다),
 * 목표금액 지점은 흰 마커 + 하단 라벨(미달이면 목표금액·도달하면 "목표금액 달성")로 표시한다.
 * 응모권 카운터(leading)는 하단 행 좌측에 목표금액과 나란히 둔다.
 * 허브 카드·응모 결과 시트가 공유한다.
 */
export const LottoPotGauge = t0 => {
  const $ = _c(36);
  const {
    fillPercent,
    goalPercent,
    goalLabel,
    accumulatedAmountLabel,
    leading,
    isCelebrating: t1
  } = t0;
  const isCelebrating = t1 === undefined ? false : t1;
  const anchorPercent = fillPercent;
  const t2 = `${anchorPercent}%`;
  const t3 = `translateX(-${anchorPercent}%)`;
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = {
      left: t2,
      transform: t3
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isCelebrating) {
    t5 = isCelebrating ? <span aria-hidden={true} className="pointer-events-none absolute inset-0 rounded-xsmall shadow-[0_0_16px_6px] shadow-O-550/45" /> : null;
    $[3] = isCelebrating;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== accumulatedAmountLabel) {
    t6 = <span className="font-700 tabular-nums">{accumulatedAmountLabel}</span>;
    $[5] = accumulatedAmountLabel;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `clamp(12px, ${anchorPercent}%, calc(100% - 12px))`;
  let t8;
  if ($[7] !== t7) {
    t8 = <span aria-hidden={true} className="absolute -bottom-4 size-8 -translate-x-1/2 rotate-45 bg-Gray-700 transition-[left] duration-300 motion-reduce:transition-none" style={{
      left: t7
    }} />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t8) {
    t9 = <div className="relative flex min-h-36 items-end"><span className="absolute bottom-0 flex items-center gap-4 whitespace-nowrap rounded-xsmall bg-Gray-700 px-12 py-8 text-Gray-white transition-[left,transform] duration-300 typo-caption1 motion-reduce:transition-none" style={t4}>{t5}누적 금액{" "}{t6}{t8}</span></div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = `${fillPercent}%`;
  let t11;
  if ($[14] !== t10) {
    t11 = <div className="h-full rounded-pill bg-gradient-to-r from-G-300 to-Y-550 transition-[width] duration-300 motion-reduce:transition-none" style={{
      width: t10
    }} />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== isCelebrating) {
    t12 = isCelebrating ? <span aria-hidden={true} className={cn("pointer-events-none absolute inset-0 rounded-pill shadow-[0_0_14px_4px] shadow-O-550/60", styles.feverBarPulse)} /> : null;
    $[16] = isCelebrating;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = `${goalPercent}%`;
  let t14;
  if ($[18] !== t13) {
    t14 = <span aria-hidden={true} className="absolute top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-pill bg-Gray-white" style={{
      left: t13
    }} />;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t14) {
    t15 = <div className="relative h-10 w-full rounded-pill bg-Gray-300">{t11}{t12}{t14}</div>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = `${goalPercent}%`;
  let t17;
  if ($[24] !== t16) {
    t17 = {
      left: t16
    };
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== goalLabel || $[27] !== t17) {
    t18 = <span className="absolute -translate-x-1/2 whitespace-nowrap font-600 text-Gray-700 typo-caption2" style={t17}>{goalLabel}</span>;
    $[26] = goalLabel;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== leading || $[30] !== t18) {
    t19 = <div className="relative flex min-h-16 items-center">{leading}{t18}</div>;
    $[29] = leading;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t15 || $[33] !== t19 || $[34] !== t9) {
    t20 = <div className="relative flex flex-col gap-8">{t9}{t15}{t19}</div>;
    $[32] = t15;
    $[33] = t19;
    $[34] = t9;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
};